import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { transformLocale } from "@/utils/transformDate";
import { useRouter } from "next/router";
import { BackIcon } from "@/components/RFIcon";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "../ui/select";
interface CalendarNavigationProps {
	onNext: (val: Dayjs) => void;
	minDate: Dayjs;
	maxDate: Dayjs;
	date: Dayjs;
}

export default function BirthDayNavigation(props: CalendarNavigationProps) {
	const month = props.date.month() + 1;
	const year = props.date.year();

	const handlePre = (disabled: boolean) => {
		if (disabled) return;
		const _date = props.date.add(-1, "M");
		props.onNext(_date);
	};
	const handleNext = (disabled: boolean) => {
		if (disabled) return;
		const _date = props.date.add(1, "M");
		props.onNext(_date);
	};

	const buttons = useMemo(() => {
		const now = dayjs();
		// 取出年月比较
		const currentDate = now.set("year", props.date.year()).set("month", props.date.month()).set("date", 1);
		const preDate = now.set("year", props.minDate.year()).set("month", props.minDate.month()).set("date", 1);

		const nextDate = now.set("year", props.maxDate.year()).set("month", props.maxDate.month()).set("date", 1);

		const preDisabled = preDate.valueOf() >= currentDate.valueOf();
		const nextDisabled = currentDate.valueOf() >= nextDate.valueOf();

		return [
			{ type: "pre", onClick: handlePre, disabled: preDisabled },
			{ type: "next", onClick: handleNext, disabled: nextDisabled },
		];
	}, [props.maxDate, props.minDate, props.date]);

	return (
		<div className="flex items-center justify-between py-4">
			<div className="flex items-center space-x-2">
				<SelectMonth
					month={month}
					onSelect={(_month) => {
						const newDate = props.date.set("month", _month - 1);
						props.onNext(newDate);
					}}
				/>
				<SelectYear
					year={year}
					minDate={props.minDate}
					maxDate={props.maxDate}
					onSelect={(_year) => {
						const newDate = props.date.set("year", _year);
						props.onNext(newDate);
					}}
				/>
			</div>
			<div className="flex items-center">
				{buttons.map((item) => (
					<span
						key={item.type}
						className={clsx(
							"text-md ml-6 flex items-center justify-center rounded text-2xl cursor-pointer",
							item.disabled ? "text-c3c3c3 dark:text-858585" : "text-dark dark:text-white",
						)}
						onClick={() => item.onClick(item.disabled)}
					>
						<BackIcon className={clsx("w-4 h-4", item.type === "next" ? "rotate-180" : "rotate-0")} strokeWidth={2} />
					</span>
				))}
			</div>
		</div>
	);
}

function SelectMonth(props: { month: number; onSelect: (month: number) => void }) {
	const router = useRouter();
	const [months, setMonths] = useState<{ label: string; value: number }[]>([]);
	useEffect(() => {
		const locale = router.locale || "";
		const dayjsLocale = transformLocale(locale);
		const newMonths = new Array(12).fill(0).map((_, index) => {
			const label = dayjs().locale(dayjsLocale).month(index).format("MMMM");
			return { label, value: index + 1 };
		});
		setMonths(newMonths);
	}, [router.locale]);

	const selectedLabel = months.find((m) => m.value === props.month)?.label ?? props.month.toString();

	return (
		<Select
			value={props.month.toString()}
			onValueChange={(value) => {
				props.onSelect(parseInt(value));
			}}
		>
			<SelectTrigger>
				<span>{selectedLabel}</span>
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					{months.map((m) => (
						<SelectItem key={m.value} value={m.value.toString()}>
							{m.label}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}

function SelectYear(props: { year: number; minDate: Dayjs; maxDate: Dayjs; onSelect: (year: number) => void }) {
	const [years, setYears] = useState<number[]>([]);
	useEffect(() => {
		const minYear = props.minDate.year();
		const maxYear = props.maxDate.year();
		const years = [];
		for (let y = minYear; y <= maxYear; y++) {
			years.push(y);
		}
		setYears(years.sort((a, b) => b - a));
	}, [props.maxDate, props.minDate]);

	return (
		<Select
			value={props.year.toString()}
			onValueChange={(value) => {
				props.onSelect(parseInt(value));
			}}
		>
			<SelectTrigger>
				<span>{props.year}</span>
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					{years.map((year, _) => (
						<SelectItem key={year} value={year.toString()}>
							{year}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}
